import { Component, Input, OnChanges, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Employer } from 'src/app/models/employer';
import { TranslationService } from 'src/app/services/translation.service';
import { SiteService } from 'src/app/services/site.service';
import { CrisisCardComponent } from '../crisis-card/crisis-card.component';
import { TranslateDirective } from 'src/app/directives/translate.directive';

interface CommunityCard {
	icon: string;
	head: string;
	body: string;
	link: string;
	headTranslateId: string;
	bodyTranslateId: string;
}

@Component({
  selector: 'app-crisis-cards-section',
  templateUrl: './crisis-cards-section.component.html',
  styleUrls: ['./crisis-cards-section.component.scss']
})
export class CrisisCardsSectionComponent implements OnInit, OnChanges, OnDestroy {

	@Input() data: any;
	@Input() employer: Employer;
	@ViewChildren(CrisisCardComponent) crisisCards: QueryList<CrisisCardComponent>;
	@ViewChildren(TranslateDirective) translateDirectives: QueryList<TranslateDirective>;

	// A11: on mobile the community cards render 2 at a time behind a Load More
	// button (same pattern as Learn More). The note is mobile-scoped, so desktop
	// keeps showing every card at once.
	readonly communityCardsPerGroup = 2;
	communityGroupsVisible = 1;
	isMobile = false;

	private destroy$ = new Subject<void>();

	private _communityCards: CommunityCard[] = [];

	/**
	 * Built once per input change, NOT recomputed per getter call.
	 *
	 * These objects are the `*ngFor` identity for the crisis cards. A getter that
	 * returns fresh literals gives Angular a new identity every change-detection
	 * pass, so it destroys and recreates each <app-crisis-card>, whose ngOnInit
	 * resets displayHead/displayBody back to the English @Input. The translation
	 * lands, then the next tick wipes it - which is why the Community Support
	 * cards stayed English in every language while the Crisis Support cards
	 * above them (still static markup) translated fine.
	 */
	private buildCommunityCards(): void {
		const cards: CommunityCard[] = [];
		if (this.employer?.includeLocalResources) {
			cards.push({ icon: 'heart', head: 'Get Local Help Now', body: this.data?.['Text'], link: this.data?.['URL'], headTranslateId: 'crisis-local-head', bodyTranslateId: 'crisis-local-body' });
		}
		cards.push({ icon: 'search', head: 'FindTreatment.GOV', body: 'FindTreatment.gov offers a confidential way to seek treatment for mental health and substance use across the United States.', link: 'https://findtreatment.gov', headTranslateId: 'crisis-findtreatment-head', bodyTranslateId: 'crisis-findtreatment-body' });
		cards.push({ icon: 'star', head: 'Veterans, Find Support', body: 'Any time day or night. You don\'t have to be enrolled in VA benefits or health care to connect.', link: 'https://www.veteranscrisisline.net/', headTranslateId: 'crisis-veterans-head', bodyTranslateId: 'crisis-veterans-body' });
		cards.push({ icon: '211', head: 'Connect to 211', body: '211 is your 24 hr lifeline to a free, confidential, and compassionate professional who will review your situation and connect you with resources to meet your needs.', link: 'https://211.org/', headTranslateId: 'crisis-211-head', bodyTranslateId: 'crisis-211-body' });
		this._communityCards = cards;
	}

	/**
	 * Identity for the *ngFor. headTranslateId is unique and stable per card, so
	 * Angular reuses the existing <app-crisis-card> instead of recreating it -
	 * which would reset the card's translated text back to English.
	 */
	trackCard(_index: number, card: CommunityCard): string {
		return card.headTranslateId;
	}

	get communityCards(): CommunityCard[] {
		return this._communityCards;
	}

	get visibleCommunityCards() {
		if (!this.isMobile) { return this.communityCards; }
		return this.communityCards.slice(0, this.communityGroupsVisible * this.communityCardsPerGroup);
	}

	get hasMoreCommunityCards(): boolean {
		return this.isMobile && this.communityCards.length > this.visibleCommunityCards.length;
	}

	loadMore(): void {
		this.communityGroupsVisible++;
		// newly revealed cards need translating when a non-English language is active
		setTimeout(() => this.translateCrisisCards(), 100);
	}

	constructor(private translationService: TranslationService, private siteService: SiteService) {}

	ngOnChanges(): void {
		this.buildCommunityCards();
	}

	ngOnInit(): void {
		this.buildCommunityCards();

		// Load More is mobile-only; onResize replays the current size on subscribe
		this.siteService.onResize.pipe(
			takeUntil(this.destroy$)
		).subscribe(size => {
			const wasMobile = this.isMobile;
			this.isMobile = size === 's';
			if (wasMobile && !this.isMobile) { this.communityGroupsVisible = 1; }
			if (!wasMobile && this.isMobile) { setTimeout(() => this.translateCrisisCards(), 100); }
		});

		// Listen for language changes
		this.translationService.currentLanguage$.pipe(
			takeUntil(this.destroy$)
		).subscribe(() => {
			if (this.employer) {
				this.translateCrisisCards();
			}
		});

		// Initial translation after view init
		setTimeout(() => this.translateCrisisCards(), 100);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Translate crisis card content
	 */
	private translateCrisisCards(): void {
		if (!this.employer || !this.employer.employerID) {
			return;
		}

		const items: Array<{ id: string; text: string }> = [];

		// Collect from translate directives (section headers)
		if (this.translateDirectives) {
			this.translateDirectives.forEach(directive => {
				const id = directive.getId();
				const text = directive.getOriginalText();
				if (id && text) {
					items.push({ id, text });
				}
			});
		}

		// Collect translatable items from crisis cards
		if (this.crisisCards) {
			this.crisisCards.forEach((card) => {
				if (card.headTranslateId && card.head) {
					items.push({ id: card.headTranslateId, text: card.head });
				}
				if (card.bodyTranslateId && card.body) {
					items.push({ id: card.bodyTranslateId, text: card.body });
				}
			});
		}

		if (items.length === 0) {
			return;
		}

		// Get the page path from the employer or use a default
		const pagePath = '/crisis-support';

		// Call translation service
		this.translationService.translatePage(
			this.employer.employerID,
			pagePath,
			items
		).subscribe(translations => {
			// Apply translations to directives (section headers)
			if (this.translateDirectives) {
				this.translateDirectives.forEach(directive => {
					const id = directive.getId();
					if (translations[id]) {
						directive.applyTranslation(translations[id]);
					}
				});
			}

			// Apply translations to crisis cards
			if (this.crisisCards) {
				this.crisisCards.forEach((card) => {
					if (card.headTranslateId && translations[card.headTranslateId]) {
						card.applyHeadTranslation(translations[card.headTranslateId]);
					}
					if (card.bodyTranslateId && translations[card.bodyTranslateId]) {
						card.applyBodyTranslation(translations[card.bodyTranslateId]);
					}
				});
			}
		});
	}
}
